import axios from 'axios';
import api from './api';

export const loginUser = async (email, password) => {
  try {
    // Create a new axios instance without interceptors for the login request
    const loginApi = axios.create({
      baseURL: 'https://unitrade-backend-946167918479.europe-west1.run.app/api',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      withCredentials: true,
      xsrfCookieName: 'csrftoken',
      xsrfHeaderName: 'X-CSRFToken',
      withXSRFToken: true
    });
    
    // Make the login request without interceptors
    const response = await loginApi.post('/users/login/', 
      { email, password },
      {
        validateStatus: function (status) {
          return status >= 200 && status < 500; // Resolve only if the status code is less than 500
        }
      }
    );
    
    if (response.data.access && response.data.user) {
      // Store tokens and user data
      localStorage.setItem('token', response.data.access);
      localStorage.setItem('refreshToken', response.data.refresh);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('userRole', response.data.user.role);
      
      // Update the default axios instance with the new token
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;
      
      return response.data.user;
    }
    
    throw new Error('Invalid server response');
  } catch (err) {
    console.error('Login error:', err);
    
    // Create error object with default values
    const loginError = new Error('An unexpected error occurred. Please try again.');
    loginError.fieldErrors = {};
    
    // Handle network errors
    if (!err.response) {
      loginError.message = err.message === 'Network Error' 
        ? 'Unable to connect to the server. Please check your internet connection.'
        : 'An unexpected error occurred. Please try again.';
      throw loginError;
    }
    
    // Handle HTTP response errors
    const { status, data } = err.response;
    
    // Set default error message based on status code
    if (status === 400) {
      loginError.message = 'Please provide valid login information.';
      // Map field-specific errors if available
      if (data.email) {
        loginError.fieldErrors.email = Array.isArray(data.email) ? data.email[0] : data.email;
      }
      if (data.password) {
        loginError.fieldErrors.password = Array.isArray(data.password) ? data.password[0] : data.password;
      }
      if (data.non_field_errors) {
        loginError.message = Array.isArray(data.non_field_errors) 
          ? data.non_field_errors[0] 
          : data.non_field_errors;
      }
    } else if (status === 401) {
      loginError.message = data.detail || 'Incorrect email or password. Please try again.';
    } else if (status === 403) {
      loginError.message = data.detail || 'Your account is not active. Please contact support.';
    } else if (status === 404) {
      loginError.message = 'No account found with this email address.';
    } else if (status >= 500) {
      loginError.message = 'Server error. Please try again later.';
    } else if (err.response.data?.detail) {
      loginError.message = err.response.data.detail;
    } else if (err.response.data?.non_field_errors) {
      loginError.message = Array.isArray(err.response.data.non_field_errors) 
        ? err.response.data.non_field_errors[0]
        : err.response.data.non_field_errors;
    } else if (err.response.data?.email) {
      loginError.message = Array.isArray(err.response.data.email)
        ? err.response.data.email[0]
        : err.response.data.email;
      loginError.fieldErrors = { email: loginError.message };
    } else if (err.response.data?.password) {
      loginError.message = Array.isArray(err.response.data.password)
        ? err.response.data.password[0]
        : err.response.data.password;
      loginError.fieldErrors = { password: loginError.message };
    }
    
    throw loginError;
  }
};

export const logoutUser = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userRole');
};

// Function to refresh the access token using the refresh token
export const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Create a new axios instance to avoid interceptors that might cause infinite loops
    const response = await api.post('/users/token/refresh/', {
      refresh: refreshToken
    }, {
      // Skip auth interceptor for this request
      skipAuthInterceptor: true
    });

    if (response.data.access) {
      localStorage.setItem('token', response.data.access);
      return response.data.access;
    } else {
      throw new Error('Failed to refresh token');
    }
  } catch (err) {
    console.error('Error refreshing token:', err);
    // If refresh fails, log the user out
    logoutUser();
    throw err;
  }
};

export const getCurrentUser = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    // First check if we have existing user data in localStorage
    const existingUserData = localStorage.getItem('user');
    let userData = null;
    
    if (existingUserData) {
      try {
        userData = JSON.parse(existingUserData);
      } catch (e) {
        console.warn('Failed to parse existing user data:', e);
      }
    }
    
    // Try to get fresh data from the server
    try {
      const response = await api.get('/users/me/');
      if (response.data.success) {
        // If we have existing data, merge it with the new data
        if (userData) {
          userData = {
            ...userData,
            ...response.data.user,
            // Preserve locally updated fields
            firstName: userData.firstName || response.data.user.firstName,
            lastName: userData.lastName || response.data.user.lastName,
            phoneNumber: userData.phoneNumber || response.data.user.phoneNumber,
            department: userData.department || response.data.user.department,
            level: userData.level || response.data.user.level,
            course: userData.course || response.data.user.course,
            location: userData.location || response.data.user.location,
            bio: userData.bio || response.data.user.bio,
            photoURL: userData.photoURL || response.data.user.photoURL
          };
        } else {
          userData = response.data.user;
        }
        
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('userRole', userData.role);
      }
    } catch (apiErr) {
      console.warn('Could not fetch user from API, using cached data:', apiErr);
      // If API call fails but we have cached data, use that instead of logging out
      if (!userData) {
        throw apiErr; // Only throw if we don't have cached data
      }
    }
    
    return userData;
  } catch (err) {
    console.error('Error fetching current user:', err.response?.data?.message || err.message);
    logoutUser();
    return null;
  }
};

export const registerUser = async (userData) => {
  try {
    // Updated endpoint for Django backend
    const response = await api.post('/users/register/', userData);
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Registration failed';
    console.error('Error in registerUser:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const sendOtp = async (email) => {
  try {
    // Updated endpoint for Django backend
    const response = await api.post('/users/send-otp/', { email });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to send OTP';
    console.error('Error in sendOtp:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const verifyOtp = async (email, otp) => {
  try {
    // Updated endpoint for Django backend
    const response = await api.post('/users/verify-otp/', { email, otp });
    return response.data;
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to verify OTP';
    console.error('Error in verifyOtp:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const updateUserProfile = async (userData) => {
  try {
    // Special handling for profile picture uploads
    if (userData instanceof FormData || (userData.profile_picture && userData.profile_picture instanceof File)) {
      // Create a new FormData if not already FormData
      const formData = userData instanceof FormData ? userData : new FormData();
      
      // If userData is not FormData, add the file to formData
      if (!(userData instanceof FormData) && userData.profile_picture instanceof File) {
        formData.append('profile_picture', userData.profile_picture);
        
        // Add other fields if present
        Object.entries(userData).forEach(([key, value]) => {
          if (key !== 'profile_picture' && value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });
      }
      
      console.log('Sending profile picture FormData');
      
      // Make the API request with FormData
      const response = await api.put('/users/profile/', formData, {
        headers: {
          // Let the browser set the Content-Type header with boundary
          'Content-Type': 'multipart/form-data',
        }
      });
      
      console.log('Profile picture update response:', response.data);
      
      if (response.data.success) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data.user;
      }
      
      return response.data.user;
    } else {
      // Regular profile update (no files)
      console.log('Sending regular profile update:', userData);
      
      const response = await api.put('/users/profile/', userData, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('Profile update response:', response.data);
      
      if (response.data.success) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data.user;
      }
      
      return response.data.user;
    }
  } catch (err) {
    console.error('Error response:', err.response?.data);
    
    let errorMessage = 'Failed to update profile';
    if (err.response?.data) {
      if (typeof err.response.data === 'object') {
        // Extract error messages from the response object
        const errors = Object.entries(err.response.data)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join('; ');
        errorMessage = errors || 'Failed to update profile';
      } else if (typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      }
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    console.error('Error in updateUserProfile:', errorMessage);
    throw new Error(errorMessage);
  }
};

export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await api.post('/users/change-password/', {
      old_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  } catch (err) {
    console.error('Error changing password:', err);
    const errorMessage = err.response?.data?.message || err.response?.data?.detail || 'Failed to change password';
    throw new Error(errorMessage);
  }
};

export const finalizeVerification = async (uid) => {
  try {
    const response = await api.post('/finalize-verification', { uid });
    return response.data;
  } catch (err) {
    console.error('Error finalizing verification:', err.response?.data?.message || err.message);
    // We don't throw an error here as this is often a background task
    // and shouldn't block the user's login flow.
    return { success: false, message: err.response?.data?.message || 'Could not finalize verification' };
  }
};
